import React from 'react';
import { motion } from 'framer-motion';
import { useGame } from '../context/GameContext';
import { NoirBackground, Button, GameCard, Pill } from '../components/GameUI';
import { User, VenetianMask, Ghost, Award, RotateCcw, Home } from 'lucide-react';

const Results = () => {
  const { winner, players, wordPair, resetGame, resetForNewRound } = useGame();
  const wc = () => winner.winner === 'civilians' ? '#E0DDD5' : winner.winner === 'imposters' ? '#EF4444' : '#FFFFFF';
  const rc = (r) => r === 'civilian' ? '#E0DDD5' : r === 'imposter' ? '#EF4444' : '#FFFFFF';
  const ri = (r) => r === 'civilian' ? <User size={14} strokeWidth={2} /> : r === 'imposter' ? <VenetianMask size={14} strokeWidth={2} /> : <Ghost size={14} strokeWidth={2} />;
  const rl = (r) => r === 'civilian' ? 'Civilian' : r === 'imposter' ? 'Imposter' : 'Mr. White';

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center p-6 relative select-none overflow-hidden">
      <NoirBackground />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md space-y-8 relative z-20">
        {/* Header */}
        <div className="text-center space-y-4">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.2 }}>
            <Award size={44} style={{ color: wc() }} className="mx-auto" />
          </motion.div>
          <div className="font-mono text-[#5A5A5A] text-xs uppercase tracking-[0.4em]">Case Closed</div>
          <div className="red-line w-full" />
          <motion.h1 className="font-typewriter text-5xl" style={{ color: wc() }}
            initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.3 }}>
            {winner.winner === 'civilians' ? 'Civilians Win' : winner.winner === 'imposters' ? 'Imposters Win' : 'Mr. White Wins'}
          </motion.h1>
        </div>

        {/* Evidence */}
        <GameCard className="text-center space-y-4 p-8">
          <div>
            <div className="font-mono text-[#5A5A5A] text-xs uppercase tracking-wider">Civilian Word</div>
            <div className="font-typewriter text-2xl text-[#E0DDD5] mt-1">{wordPair.civilian}</div>
          </div>
          <div className="red-line w-8 mx-auto" />
          <div>
            <div className="font-mono text-[#5A5A5A] text-xs uppercase tracking-wider">Imposter Word</div>
            <div className="font-typewriter text-2xl text-[#EF4444] mt-1">{wordPair.imposter}</div>
          </div>
        </GameCard>

        {/* Suspects */}
        <div className="space-y-3">
          <div className="font-mono text-[#5A5A5A] text-xs uppercase tracking-[0.2em]">All Suspects</div>
          {players.map(p => (
            <div key={p.id} className="bg-gradient-to-b from-[#161616] to-[#111] border border-[#222] rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span style={{ color: rc(p.role) }}>{ri(p.role)}</span>
                <span className="text-[#E0DDD5] font-bold text-sm">{p.name}</span>
                {p.isEliminated && <span className="font-mono text-[#333] text-xs">(Eliminated)</span>}
              </div>
              <span className="font-mono text-xs uppercase tracking-wider" style={{ color: rc(p.role) }}>{rl(p.role)}</span>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <Button data-testid="play-again-same-players-btn" color="red" onClick={resetForNewRound}>
            <span className="flex items-center justify-center gap-2"><RotateCcw size={16} /> Reopen Case</span>
          </Button>
          <button data-testid="new-game-btn" onClick={resetGame}
            className="btn-noir w-full py-3.5 rounded-lg font-mono text-sm uppercase tracking-wider bg-gradient-to-b from-[#1A1A1A] to-[#111] border border-[#222] text-[#5A5A5A] flex items-center justify-center gap-2">
            <Home size={14} /> New Case
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Results;
